import { Form, Formik } from "formik";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AlertComponent,
  DescriptionInputComponent,
  SelectInputComponent,
  TextInputComponent,
} from "../../../../../common/components";
import { INVOICE_STATUS } from "../../order-management-helper";
import "./create-invoice.scss";
import DatePickerComponent from "../../../../../common/components/date-picker/date-picker-compoenent";
import dayjs, { Dayjs } from "dayjs";
import { createInvoice, getAllOrders, getOrders } from "../../../../../api/order-management";
import { useSetLoading } from "../../../../../common/hooks/appearance";
import Invoice from "./invoice-pdf-preview/invoice-pdf";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import CloseIcon from "@mui/icons-material/Close";
import { getTenantDetails } from "../../../../../api/tenets";
import * as Yup from "yup";

interface CreateInvoiceProps {
  setIsOpen: (isOpen: boolean) => void;
  selectedOrder?: any;
  invoiceNumber?: string;
  selectedTenantId?: number | null;
  dueDate?: any;
}
interface Order {
  id: string;
  customer_name: string;
  total_amount: number; // Added missing property
}

interface FilteredOrder {
  value: string;
  totalAmount: number;
}

const CreateInvoice: React.FC<CreateInvoiceProps> = ({
  setIsOpen,
  selectedOrder,
  invoiceNumber,
  selectedTenantId,
  dueDate,
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState<{
    isOpen?: boolean;
    message?: any;
    type?: string;
  }>({});
  const [userData, setUserData] = useState<any[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const invoiceRef = useRef(null);
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [amount, setAmount] = useState<string>("");
  const [issuedDate, setIssuedDate] = useState<Dayjs | null>(null);

  const initialValues = {
    customer: userData.length > 0 ? userData[0].value : "",
    invoiceId: invoiceNumber,
    totalAmount: "",
    status: "",
    issueDate: "",
    dueDate: dueDate,
    reason: "",
  };

  const invoiceValidationSchema = Yup.object().shape({
    invoiceId: Yup.string().required("Invoice ID is required"),
    status: Yup.string().required("Status is required"),
    customer: Yup.string().required("Customer is required"),
    totalAmount: Yup.number()
      .typeError("Total amount must be a number")
      .required("Amount is required"),
    issueDate: Yup.date().required("Invoice Date is required").nullable(),
    dueDate: Yup.date().nullable(),
    reason: Yup.string().when("status", {
      is: "FAILED",
      then: (schema) =>
        schema.required("Reason is required when status is FAILED"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getAllOrders();
      const rawOrders = response?.data?.data ?? [];

      const filteredOrders: FilteredOrder[] = (rawOrders as Order[])
        .filter((order) => order.id === selectedOrder)
        .map((order) => ({
          value: order.customer_name,
          totalAmount: order.total_amount,
        }));

      setUserData(filteredOrders);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
      setAlert({
        isOpen: true,
        message: "Failed to fetch orders. Please try again.",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedOrder) {
      fetchOrders();
    }
  }, [selectedOrder, fetchOrders]);

  const fetchTenantData = async () => {
    try {
      setIsLoading(true);

      const response = await getTenantDetails({ id: selectedTenantId ?? 0 });
      const tenantData = response.data?.data ?? [];

      const selectedPlan = tenantData.purchased_subscription_plan
        ? tenantData?.purchased_subscription_plan?.plan_name
        : null;
      const companydetails = tenantData.basic_details
        ? {
            companyName: tenantData?.basic_details?.company_name,
            email: tenantData?.basic_details?.email,
          }
        : null;
      const companyAddress = tenantData ? tenantData?.company_address : null;

      setOrderDetails({
        selectedPlan,
        companydetails,
        companyAddress,
      });

      setIsLoading(false);
    } catch (error: any) {
      console.error("Error fetching user profile:", error);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTenantId) {
      fetchTenantData();
    }
  }, [selectedTenantId]);

  const handleDownloadPdf = async () => {
    const element = invoiceRef.current;
    if (!element) return;

    const canvas = await html2canvas(element, { scale: 2 });
    const data = canvas.toDataURL("image/png");

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "px",
      format: "a4",
    });

    const imgProps = pdf.getImageProperties(data);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

    pdf.addImage(data, "PNG", 0, 0, pdfWidth, pdfHeight);
    const invoiceId = invoiceNumber;
    const timestamp = new Date().toISOString().split("T")[0];
    const fileName = invoiceNumber
      ? `${invoiceId}-${timestamp}.pdf`
      : `invoice-${timestamp}.pdf`;

    pdf.save(fileName);
  };

  const handleSave = async (values: any, resetForm: any) => {
    setIsLoading(true);
    try {
      const invoiceData = {
        orderId: selectedOrder,
        invoiceNumber: values.invoiceId,
        totalAmount: parseFloat(values.totalAmount || "0").toFixed(2),
        status: values.status,
        invoiceDate: values.issueDate ? values.issueDate.toISOString() : null,
        dueDate: values.dueDate ? values.dueDate.toISOString() : null,
      };
      const response = await createInvoice(invoiceData);
      setAlert({
        isOpen: true,
        message: response?.data?.data?.message || "Invoice saved successfully!",
        type: "success",
      });
      setIsSubmitted(true);
    } catch (error) {
      console.error("Error saving invoice:", error);
      setAlert({
        isOpen: true,
        message: "Failed to save invoice. Please try again.",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  return (
    <div
      className={`invoice-create-wrapper ${showPreview ? "preview-mode" : ""}`}
    >
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={invoiceValidationSchema}
        onSubmit={(values, { resetForm }) => handleSave(values, resetForm)}
      >
        {({
          values,
          setFieldValue,
          handleSubmit,
          resetForm,
          validateForm,
          setFieldTouched,
          touched,
          errors,
        }) => {
          return (
            <Form className="invoice-create-form" style={{ flex: 1 }}>
              <div className="invoice-form-scrollable">
                <div className="invoice-form-header">
                  <h2>Invoice Details</h2>
                  <p>Select fields to add in the form</p>
                </div>
                <div className="invoice-form-wrapper">
                  <SelectInputComponent
                    fieldName="customer"
                    label="Customer"
                    placeholder="Select Customer"
                    isRequired={true}
                    onChange={(e: any) => setFieldValue("customer", e)}
                    value={values.customer}
                    isShowError={!!touched.status && !!errors.status}
                    options={!isSubmitted ? userData : []}
                    disabled={!!isSubmitted || !invoiceNumber}
                  />
                  <TextInputComponent
                    type="text"
                    fieldName="invoiceId"
                    label="Invoice ID"
                    placeholder="Enter Invoice ID"
                    value={values.invoiceId}
                    onChange={(e: any) =>
                      setFieldValue("invoiceId", e?.target?.value)
                    }
                    isRequired={true}
                    isName={true}
                    minlength="3"
                    maxlength="50"
                    disabled={!!invoiceNumber}
                  />
                  <SelectInputComponent
                    fieldName="status"
                    label="Status"
                    options={!isSubmitted ? INVOICE_STATUS : []}
                    placeholder="Select Status"
                    onChange={(e: any) => setFieldValue("status", e)}
                    value={values.status}
                    disabled={!!isSubmitted || !invoiceNumber}
                    isRequired={true}
                    isShowError={!!touched.status && !!errors.status}
                  />
                  <TextInputComponent
                    type="text"
                    fieldName="totalAmount"
                    label="Total Amount"
                    placeholder="Enter total amount"
                    value={values.totalAmount}
                    onChange={(e: any) => {
                      const input = e?.target?.value;
                      const sanitized = input.replace(/[^0-9.]/g, "");
                      setFieldValue("totalAmount", sanitized);
                      setAmount(sanitized);
                    }}
                    isRequired={true}
                    minlength="3"
                    maxlength="50"
                    disabled={!!isSubmitted || !invoiceNumber}
                  />
                  <DatePickerComponent
                    fieldName="issueDate"
                    label="Invoice Date"
                    value={values?.issueDate ? dayjs(values.issueDate) : null}
                    onChange={(date) => {
                      setFieldValue("issueDate", date);
                      setIssuedDate(date);
                    }}
                    minDate={dayjs("1900-01-01")}
                    maxDate={dayjs()}
                    optional={true}
                    disabled={!!isSubmitted || !invoiceNumber}
                    useFormikError={true}
                    hasBorder={true}
                  />
                  <DatePickerComponent
                    fieldName="dueDate"
                    label="Due Date"
                    value={values?.dueDate ? dayjs(values.dueDate) : null}
                    onChange={(date) => setFieldValue("dueDate", date)}
                    minDate={dayjs("1900-01-01")}
                    maxDate={dayjs()}
                    optional={true}
                    disabled={!!isSubmitted || !!dueDate}
                  />
                  {values.status === "FAILED" && (
                    <div className="failed-description">
                      <label className="failed-description-label">
                        Reason for Failure
                      </label>
                      <DescriptionInputComponent
                        fieldName="reason"
                        placeholder="Provide reason"
                        value={values.reason}
                        onChange={(e: any) =>
                          setFieldValue("reason", e?.target?.value)
                        }
                        isRequired={true}
                        disabled={!!isSubmitted || !invoiceNumber}
                        rows={4}
                      />
                    </div>
                  )}
                </div>
                {!showPreview ? (
                  <div className="invoice-form-footer">
                    <button
                      type="button"
                      className="button-outlined"
                      onClick={() => {
                        resetForm();
                      }}
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      className="submit-button"
                      onClick={async () => {
                        const errors = await validateForm();
                        if (Object.keys(errors).length === 0) {
                          setPreviewData(values);
                          setShowPreview(true);
                        } else {
                          Object.keys(errors).forEach((field) => {
                            setFieldTouched(field, true, false);
                          });
                        }
                      }}
                    >
                      Preview
                    </button>
                  </div>
                ) : (
                  <div className="invoice-form-save-buttons">
                    <button
                      type="button"
                      className="download-button"
                      onClick={handleDownloadPdf}
                    >
                      Download
                    </button>

                    <div className="invoice-form-action-buttons">
                      {!isSubmitted && (
                        <button
                          type="button"
                          className="submit-button"
                          onClick={() => {
                            handleSubmit();
                          }}
                        >
                          Save
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </Form>
          );
        }}
      </Formik>

      {showPreview && (
        <div className="invoice-preview">
          <div className="invoice-preview-header">
            <h3>Preview</h3>
            {/* <CloseIcon fontSize="small" onClick={() => setShowPreview(false)} /> */}
          </div>
          <div className="invoice-preview-content">
            <Invoice
              ref={invoiceRef}
              data={previewData}
              orderData={orderDetails}
              amount={amount}
              issuedDate={issuedDate}
            />
          </div>
        </div>
      )}

      {alert?.isOpen && (
        <AlertComponent
          message={alert?.message || ""}
          type={
            ["info", "success", "error", "warning"].includes(alert?.type || "")
              ? (alert?.type as "info" | "success" | "error" | "warning")
              : "info"
          }
          open={alert?.isOpen}
          onClose={() => setAlert({})}
        />
      )}
    </div>
  );
};

export default CreateInvoice;
